import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import type { TFunction } from 'i18next';
import type { NavModuleProps } from 'vopay';
import { PartnerPermissions, usePermissions } from '@/providers/Permissions.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import { DEFAULT_MODULE } from '../constants';
import { useTranslation } from 'react-i18next';

type SubNavModule = {
  onNavClick: (currModule: string) => void;
  goBackInNavLeft: () => void;
  getSubNavModules: (parentModule?: string) => NavModuleProps[];
  reset: () => void;
  parentModules: string[];
  label: string;
  description: string;
  subNavLabel: string;
  hasParent: boolean;
};

const useSubModule = (): SubNavModule => {
  const accountFeatureFlags = useModuleFeatureFlag('accounts');
  const billingPackageFeatureFlags = useModuleFeatureFlag('billingPackages');
  const reportsFeatureFlags = useModuleFeatureFlag('reports');
  const history = useHistory();
  const { permissions } = usePermissions();

  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UseSubModule' });

  const [parentModules, setParentModules] = useState<string[]>([]);
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [hasParent, setHasParent] = useState(false);
  const [subNavActiveModule, setSubNavActiveModule] = useState('');
  const [subNavLabel, setSubNavLabel] = useState('');

  useEffect(() => {
    if (parentModules.length === 0) setHasParent(false);
  }, [parentModules]);

  function onNavClick(currModule: string) {
    const [modLabel, modDescription] = getLabelAndDescription(currModule, t);
    setParentModules((prevState) => [...prevState, currModule]);

    if (currModule === 'dashboard') {
      setSubNavActiveModule('');
    }

    setLabel(modLabel || '');
    setDescription(modDescription || '');
    setHasParent(true);
  }

  function onSubNavClick(moduleId: string, destination: string, currLabel: string) {
    setSubNavActiveModule(moduleId);
    setSubNavLabel(currLabel);

    if (history.location.pathname !== destination) history.push(destination);
  }

  function goBackInNavLeft() {
    setParentModules((prevState) => prevState.slice(0, prevState.length - 1));
  }

  function reset() {
    setParentModules([]);
    setLabel('');
    setDescription('');
    setHasParent(false);
    setSubNavActiveModule('');
    setSubNavLabel('');
  }

  function getSubNavModules(parentModule?: string) {
    return getModuleHandler(
      parentModule || DEFAULT_MODULE,
      subNavActiveModule,
      { ...accountFeatureFlags, ...billingPackageFeatureFlags, ...reportsFeatureFlags },
      permissions,
      onSubNavClick,
      t,
      isRoleViewOnly,
    );
  }

  return {
    onNavClick,
    goBackInNavLeft,
    getSubNavModules,
    reset,
    parentModules,
    label,
    description,
    subNavLabel,
    hasParent,
  };
};

export default useSubModule;

function getModuleHandler(
  parentModule: string,
  activeModule: string,
  featureFlags: { [feature: string]: boolean | undefined },
  permissions: PartnerPermissions,
  callback: (moduleId: string, destination: string, currLabel: string) => void,
  t: (key: string) => string,
  isRoleViewOnly: boolean,
) {
  const handlers: { [key: string]: NavModuleProps[] } = {
    dashboard: [],
    accountBilling: [],
    partners: [
      {
        id: 'navModule-partnerList',
        icon: 'accounts' as const,
        activeIcon: 'accountsOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('partnerListLabel'),
        isActive: activeModule === 'partnerList',
        subNavModules: [],
        isOpen: false,
        onClick: () => callback('partnerList', '/partners', t('partnerListPageTitle')),
        showNavModule: () => true,
      },
      {
        id: 'navModule-createPartner',
        icon: 'createSubAccount' as const,
        activeIcon: 'createSubAccountOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('createPartnerLabel'),
        isActive: activeModule === 'createPartner',
        isOpen: false,
        subNavModules: [],
        onClick: () => callback('createPartner', '/partners-create', t('createPartnerPageTitle')),
        showNavModule: () => !isRoleViewOnly,
      },
    ].filter((subModule) => subModule.showNavModule()),
    leads: [
      {
        id: 'navModule-leadSummary',
        icon: 'accounts' as const,
        activeIcon: 'accountsOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('leadSummaryLabel'),
        isActive: activeModule === 'leadSummary',
        subNavModules: [],
        isOpen: false,
        onClick: () => callback('leadSummary', '/leads', t('leadSummaryPageTitle')),
        showNavModule: () => true,
      },
      {
        id: 'navModule-addlead',
        icon: 'createSubAccount' as const,
        activeIcon: 'createSubAccountOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('addLeadLabel'),
        isActive: activeModule === 'addLead',
        isOpen: false,
        subNavModules: [],
        onClick: () => callback('addLead', '/leads-create', t('addLeadPageTitle')),
        showNavModule: () => !isRoleViewOnly,
      },
    ].filter((subModule) => subModule.showNavModule()),
    accounts: [
      {
        id: 'navModule-accountSummary',
        icon: 'briefcase' as const,
        activeIcon: 'briefcaseOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('accountSummaryLabel'),
        isActive: activeModule === 'accountSummary',
        subNavModules: [],
        isOpen: false,
        onClick: () => callback('accountSummary', '/accounts', t('accountSummaryPageTitle')),
        showNavModule: () => true,
      },
      {
        id: 'navModule-addAccount',
        icon: 'addAccount' as const,
        activeIcon: 'addAccountOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('addAccountLabel'),
        isActive: activeModule === 'addAccount',
        isOpen: false,
        subNavModules: [],
        onClick: () => callback('addAccount', '/accounts-create', t('addAccountPageTitle')),
        showNavModule: () => Boolean(featureFlags.canCreateAccount) && !isRoleViewOnly,
      },
    ].filter((subModule) => subModule.showNavModule()),
    billingPackages: [
      {
        id: 'navModule-billingPackagesSummary',
        icon: 'packages' as const,
        activeIcon: 'packagesOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('packagesLabel'),
        isActive: activeModule === 'billingPackagesSummary',
        isOpen: false,
        subNavModules: [],
        onClick: () => callback('billingPackagesSummary', '/billing-packages', t('packagesPageTitle')),
        showNavModule: () => true,
      },
      {
        id: 'navModule-addBillingPackage',
        icon: 'addPackage' as const,
        activeIcon: 'addPackageOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('addBillingPackageLabel'),
        isActive: activeModule === 'addBillingPackage',
        isOpen: false,
        subNavModules: [],
        onClick: () => callback('addBillingPackage', '/billing-packages-create', t('addBillingPackagePageTitle')),
        showNavModule: () => Boolean(featureFlags.canCreatePackage) && !isRoleViewOnly,
      },
    ].filter((subModule) => subModule.showNavModule()),
    reports: [
      {
        id: 'navModule-billingDetailsReport',
        icon: 'billing' as const,
        activeIcon: 'billingOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('billingDetailsReportLabel'),
        isActive: activeModule === 'billingDetailsReport',
        isOpen: false,
        subNavModules: [],
        onClick: () => callback('billingDetailsReport', '/reports', t('billingDetailsReportPageTitle')),
        showNavModule: () => Boolean(featureFlags.canAccessBillingDetailsReport),
      },
      {
        id: 'navModule-accountActivitySummary',
        icon: 'accountActivitySummary' as const,
        activeIcon: 'accountActivitySummaryOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('accountActivitySummaryLabel'),
        isActive: activeModule === 'accountActivitySummary',
        isOpen: false,
        subNavModules: [],
        onClick: () => callback('accountActivitySummary', '/account-activity', t('accountActivitySummaryPageTitle')),
        showNavModule: () => Boolean(featureFlags.canAccessActivityReport),
      },
      {
        id: 'navModule-reconciliation',
        icon: 'invoice' as const,
        activeIcon: 'invoiceOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('reconciliationLabel'),
        isActive: activeModule === 'reconciliation',
        isOpen: false,
        subNavModules: [],
        onClick: () => callback('reconciliation', '/reconciliation', t('reconciliationPageTitle')),
        showNavModule: () => Boolean(permissions.PartnerHasVirtualAccounts),
      },
    ].filter((subModule) => subModule.showNavModule()),
    onBoardingFormTracker: [],
    settings: [],
    support: [
      {
        id: 'navModule-ticketsList',
        icon: 'ticketList' as const,
        activeIcon: 'ticketListOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('ticketsListLabel'),
        isActive: activeModule === 'ticketsList',
        subNavModules: [],
        isOpen: false,
        onClick: () => callback('ticketsList', '/tickets', t('ticketsListPageTitle')),
        showNavModule: () => true,
      },
      {
        id: 'navModule-submitTicket',
        icon: 'ticket' as const,
        activeIcon: 'ticketOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('submitTicketLabel'),
        isActive: activeModule === 'submitTicket',
        isOpen: false,
        subNavModules: [],
        onClick: () => callback('submitTicket', '/tickets-create', t('submitTicketPageTitle')),
        showNavModule: () => !isRoleViewOnly,
      },
      {
        id: 'navModule-learningCentre',
        icon: 'learningCenter' as const,
        activeIcon: 'learningCenterOn' as const,
        iconSize: 'xsm' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('learningCentreLabel'),
        isActive: activeModule === 'learningCentre',
        subNavModules: [],
        isOpen: false,
        onClick: () => callback('learningCentre', '/learning-centre', t('learningCentrePageTitle')),
        showNavModule: () => true,
      },
    ].filter((subModule) => subModule.showNavModule()),
    transactions: [
      {
        id: 'navModule-partnerAccountTransactions',
        icon: 'transactions' as const,
        activeIcon: 'transactionsOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('partnerAccountTransactionsLabel'),
        isActive: activeModule === 'partnerAccountTransactions',
        subNavModules: [],
        isOpen: false,
        onClick: () =>
          callback(
            'partnerAccountTransactions',
            '/partner-account-transactions',
            t('partnerAccountTransactionsPageTitle'),
          ),
        showNavModule: () => true,
      },
      {
        id: 'navModule-partnerAccountTransfers',
        icon: 'accountTransfer' as const,
        activeIcon: 'accountTransferOn' as const,
        iconSize: 'md' as const,
        iconColor: 'medium-dark' as const,
        iconColorOn: 'primary-light' as const,
        label: t('partnerAccountTransfersLabel'),
        isActive: activeModule === 'partnerAccountTransfers',
        subNavModules: [],
        isOpen: false,
        onClick: () =>
          callback('partnerAccountTransfers', '/partner-account-transfers', t('partnerAccountTransfersPageTitle')),
        showNavModule: () => Boolean(permissions.AccountTransfersEnabled),
      },
    ].filter((subModule) => subModule.showNavModule()),
  };

  return handlers[parentModule];
}

function getLabelAndDescription(parentModule: string, t: TFunction) {
  const handlers: { [key: string]: string[] } = {
    dashboard: [],
    accountBilling: [],
    partners: [
      t('partnersTitle', { defaultValue: 'Partners' }),
      t('partnersDescription', { defaultValue: 'Create and manage the partners you have onboarded.' }),
    ],
    leads: [],
    accounts: [
      t('accountsTitle', { defaultValue: 'My Accounts' }),
      t('accountsDescription', {
        defaultValue: 'Overview of all VoPay accounts and the status of all funds in each account.',
      }),
    ],
    billingPackages: [
      t('billingPackagesTitle', { defaultValue: 'Billing Packages' }),
      t('billingPackagesDescription', { defaultValue: 'Assign billing packages to individual VoPay accounts.' }),
    ],
    reports: [
      t('reportsTitle', { defaultValue: 'Billing Details Report' }),
      t('reportsDescription', { defaultValue: 'Access real-time reporting for all transactions.' }),
    ],
    support: [
      t('supportTitle', { defaultValue: 'Support' }),
      t('supportDescription', { defaultValue: 'Access your support tickets & FAQ' }),
    ],
    settings: [],
    onBoardingFormTracker: [],
    transactions: [
      t('transactionsTitle', { defaultValue: 'Transactions' }),
      t('transactionsDescription', { defaultValue: 'Overview of all the list of transactions.' }),
    ],
  };

  return handlers[parentModule];
}
